'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { trackLeadFormConversion } from '@/lib/analytics';

interface LeadFormData {
  name: string;
  email: string;
  phone: string;
  destination: string;
  travelDate: string;
  passengers: string;
  budget: string;
  message: string;
  travelType: string;
}

interface Step1Errors {
  name?: string;
  email?: string;
  phone?: string;
}

interface Step2Errors {
  destination?: string;
}

const LeadCaptureWidget = () => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [isVisible, setIsVisible] = useState(false);
  const [step1Errors, setStep1Errors] = useState<Step1Errors>({});
  const [step2Errors, setStep2Errors] = useState<Step2Errors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState<LeadFormData>({
    name: '',
    email: '',
    phone: '',
    destination: '',
    travelDate: '',
    passengers: '1',
    budget: '',
    message: '',
    travelType: 'flight',
  });

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Full name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        return '';
      case 'email':
        if (!value.trim()) return 'Email address is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email';
        return '';
      case 'phone':
        if (!value.trim()) return 'Phone number is required';
        if (!/^[\d\s\+\-\(\)]{7,15}$/.test(value.replace(/\s/g, ''))) return 'Please enter a valid phone number';
        return '';
      case 'destination':
        if (!value.trim()) return 'Destination is required';
        return '';
      default:
        return '';
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (touched[name]) {
      const err = validateField(name, value);
      if (step === 1) setStep1Errors(prev => ({ ...prev, [name]: err || undefined }));
      else setStep2Errors(prev => ({ ...prev, [name]: err || undefined }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    if (step === 1) setStep1Errors(prev => ({ ...prev, [name]: err || undefined }));
    else setStep2Errors(prev => ({ ...prev, [name]: err || undefined }));
  };

  const fieldClass = (name: string, errors: Record<string, string | undefined>) => {
    const base = 'w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors';
    if (touched[name] && errors[name]) return `${base} border-red-400 bg-red-50 focus:border-red-500`;
    if (touched[name] && !errors[name] && formData[name as keyof LeadFormData]) return `${base} border-green-400 bg-green-50 focus:border-green-500`;
    return `${base} border-border focus:border-primary`;
  };

  const saveLead = (data: LeadFormData, method: string) => {
    const lead = {
      id: `lead-${Date.now()}`,
      name: data.name,
      email: data.email,
      phone: data.phone,
      whatsapp: data.phone,
      message: data.message || `Travel inquiry: ${data.destination}, ${data.travelDate}, ${data.passengers} pax, Budget: ${data.budget}`,
      bookingDetails: {
        type: data.travelType as 'flight' | 'hotel' | 'holiday',
        destination: data.destination,
        date: data.travelDate,
        passengers: parseInt(data.passengers) || 1,
      },
      timestamp: new Date().toISOString(),
      status: 'new',
      bookingType: 'lead',
      source: method,
    };
    const existing = JSON.parse(localStorage.getItem('bookingLeads') || '[]');
    existing.unshift(lead);
    localStorage.setItem('bookingLeads', JSON.stringify(existing));
  };

  const sendViaWhatsApp = () => {
    saveLead(formData, 'whatsapp-widget');
    trackLeadFormConversion({
      form_location: 'floating_widget',
      destination: formData.destination,
      travel_type: formData.travelType,
      method: 'whatsapp',
    });
    const msg = encodeURIComponent(
      `Hi! I'd like to enquire about travel booking.\n\n` +
      `\uD83D\uDC64 Name: ${formData.name}\n` +
      `\uD83D\uDCE7 Email: ${formData.email}\n` +
      `\uD83D\uDCF1 Phone: ${formData.phone}\n` +
      `\u2708\uFE0F Travel Type: ${formData.travelType}\n` +
      `\uD83D\uDCCD Destination: ${formData.destination}\n` +
      `\uD83D\uDCC5 Travel Date: ${formData.travelDate}\n` +
      `\uD83D\uDC65 Passengers: ${formData.passengers}\n` +
      `\uD83D\uDCB7 Budget: ${formData.budget}\n` +
      `\uD83D\uDCAC Message: ${formData.message || 'Please contact me with the best options.'}`
    );
    window.open(`https://wa.me/447700900000?text=${msg}`, '_blank');
    router.push('/quote-thankyou');
  };

  const handleNextStep = () => {
    const allTouched: Record<string, boolean> = { name: true, email: true, phone: true };
    setTouched(prev => ({ ...prev, ...allTouched }));
    const newErrors: Step1Errors = {};
    (['name', 'email', 'phone'] as const).forEach(f => {
      const err = validateField(f, formData[f]);
      if (err) newErrors[f] = err;
    });
    setStep1Errors(newErrors);
    if (Object.keys(newErrors).length === 0) setStep(2);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(prev => ({ ...prev, destination: true }));
    const err = validateField('destination', formData.destination);
    if (err) { setStep2Errors({ destination: err }); return; }
    saveLead(formData, 'form-widget');
    trackLeadFormConversion({
      form_location: 'floating_widget',
      destination: formData.destination,
      travel_type: formData.travelType,
      passengers: parseInt(formData.passengers) || 1,
      budget: formData.budget,
      method: 'form',
    });
    router.push('/quote-thankyou');
  };

  const reset = () => {
    setStep(1);
    setIsOpen(false);
    setStep1Errors({});
    setStep2Errors({});
    setTouched({});
    setFormData({ name: '', email: '', phone: '', destination: '', travelDate: '', passengers: '1', budget: '', message: '', travelType: 'flight' });
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Floating Buttons */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 items-end">
          <a
            href="https://wa.me/447700900000?text=Hi%20I%27d%20like%20to%20enquire%20about%20a%20travel%20booking"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-[#25D366] text-white px-4 py-3 rounded-full shadow-2xl hover:bg-[#20BA5A] transition-all duration-300 hover:scale-105 font-bold text-sm"
            title="Chat on WhatsApp"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <span className="hidden sm:inline">WhatsApp Us</span>
          </a>
          <button
            onClick={() => setIsOpen(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-accent to-[#FF8C5A] text-white px-5 py-3 rounded-full shadow-2xl hover:shadow-accent/40 transition-all duration-300 hover:scale-105 font-bold text-sm"
          >
            <Icon name="PaperAirplaneIcon" size={18} />
            <span>Get a Free Quote</span>
          </button>
        </div>
      )}

      {/* Lead Capture Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={reset} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-primary to-secondary p-5 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-heading font-black text-xl">Get Your Free Quote</h3>
                  <p className="text-white/80 text-sm mt-1">We&apos;ll respond within 1 hour!</p>
                </div>
                <button onClick={reset} className="text-white/80 hover:text-white transition-colors">
                  <Icon name="XMarkIcon" size={24} />
                </button>
              </div>
              {step > 0 && (
                <div className="flex gap-2 mt-4">
                  {[1, 2].map(s => (
                    <div key={s} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${step >= s ? 'bg-accent' : 'bg-white/30'}`} />
                  ))}
                </div>
              )}
            </div>

            <div className="p-6">
              {step === 1 ? (
                <div className="space-y-4">
                  <h4 className="font-heading font-black text-foreground">Your Details</h4>
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-1">Full Name *</label>
                    <input
                      type="text" name="name" value={formData.name}
                      onChange={handleChange} onBlur={handleBlur}
                      placeholder="John Smith"
                      className={fieldClass('name', step1Errors)}
                    />
                    {touched.name && step1Errors.name && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={12} />{step1Errors.name}
                      </p>
                    )}
                    {touched.name && !step1Errors.name && formData.name && (
                      <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                        <Icon name="CheckCircleIcon" size={12} />Looks good!
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-1">Email Address *</label>
                    <input
                      type="email" name="email" value={formData.email}
                      onChange={handleChange} onBlur={handleBlur}
                      placeholder="john@example.com"
                      className={fieldClass('email', step1Errors)}
                    />
                    {touched.email && step1Errors.email && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={12} />{step1Errors.email}
                      </p>
                    )}
                    {touched.email && !step1Errors.email && formData.email && (
                      <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                        <Icon name="CheckCircleIcon" size={12} />Looks good!
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-1">Phone / WhatsApp *</label>
                    <input
                      type="tel" name="phone" value={formData.phone}
                      onChange={handleChange} onBlur={handleBlur}
                      placeholder="+44 7700 900000"
                      className={fieldClass('phone', step1Errors)}
                    />
                    {touched.phone && step1Errors.phone && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={12} />{step1Errors.phone}
                      </p>
                    )}
                    {touched.phone && !step1Errors.phone && formData.phone && (
                      <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                        <Icon name="CheckCircleIcon" size={12} />Looks good!
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleNextStep}
                    className="w-full py-3 bg-gradient-to-r from-primary to-secondary text-white font-bold rounded-lg hover:shadow-lg transition-all duration-300"
                  >
                    Next: Travel Details &rarr;
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                  <h4 className="font-heading font-black text-foreground">Travel Details</h4>
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-1">Travel Type</label>
                    <select name="travelType" value={formData.travelType} onChange={handleChange} className="w-full px-4 py-3 border-2 border-border rounded-lg focus:border-primary focus:outline-none transition-colors">
                      <option value="flight">Flight</option>
                      <option value="hotel">Hotel</option>
                      <option value="holiday">Holiday Package</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-1">Destination *</label>
                    <input
                      type="text" name="destination" value={formData.destination}
                      onChange={handleChange} onBlur={handleBlur}
                      placeholder="e.g., Dubai, Spain, New York"
                      className={fieldClass('destination', step2Errors)}
                    />
                    {touched.destination && step2Errors.destination && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <Icon name="ExclamationCircleIcon" size={12} />{step2Errors.destination}
                      </p>
                    )}
                    {touched.destination && !step2Errors.destination && formData.destination && (
                      <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                        <Icon name="CheckCircleIcon" size={12} />Looks good!
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-bold text-foreground mb-1">Travel Date</label>
                      <input type="date" name="travelDate" value={formData.travelDate} onChange={handleChange} className="w-full px-4 py-3 border-2 border-border rounded-lg focus:border-primary focus:outline-none transition-colors" />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-foreground mb-1">Passengers</label>
                      <select name="passengers" value={formData.passengers} onChange={handleChange} className="w-full px-4 py-3 border-2 border-border rounded-lg focus:border-primary focus:outline-none transition-colors">
                        {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n} {n === 1 ? 'Person' : 'People'}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-1">Budget (GBP)</label>
                    <select name="budget" value={formData.budget} onChange={handleChange} className="w-full px-4 py-3 border-2 border-border rounded-lg focus:border-primary focus:outline-none transition-colors">
                      <option value="">Select budget range</option>
                      <option value="Under £500">Under £500</option>
                      <option value="£500 - £1,000">£500 - £1,000</option>
                      <option value="£1,000 - £2,000">£1,000 - £2,000</option>
                      <option value="£2,000 - £5,000">£2,000 - £5,000</option>
                      <option value="Over £5,000">Over £5,000</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-1">Additional Notes</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} rows={2} placeholder="Any special requirements?" className="w-full px-4 py-3 border-2 border-border rounded-lg focus:border-primary focus:outline-none transition-colors resize-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={sendViaWhatsApp}
                      className="flex items-center justify-center gap-2 bg-[#25D366] text-white py-3 rounded-lg font-bold hover:bg-[#20BA5A] transition-colors"
                    >
                      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      WhatsApp
                    </button>
                    <button
                      type="submit"
                      className="flex items-center justify-center gap-2 bg-gradient-to-r from-accent to-[#FF8C5A] text-white py-3 rounded-lg font-bold hover:shadow-lg transition-all duration-300"
                    >
                      <Icon name="PaperAirplaneIcon" size={16} />
                      Send Enquiry
                    </button>
                  </div>
                  <button type="button" onClick={() => setStep(1)} className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors">
                    &larr; Back to Your Details
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LeadCaptureWidget;
